import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { FormikContextType, useFormikContext } from 'formik'
import { Box, Stack, TextLink } from '@machobear/design-system'
import { setCover } from '@/ports'
import { Dropzone } from '@/components/dropzone'
import { toast } from '@/components/toast-notifications'
import DraggablePhotoList from './draggable-photo-list'
import { FormBox, FormBoxSkeleton, Counter } from '../../components'
import InfoBox from '../../components/info-box'
import { MediaFormValues, Image, Listing } from '../../types'

const STORAGE_LIMIT = 20

const PhotoSection = ({ data, isLoading }: { data: Listing; isLoading: boolean }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    values,
    setFieldValue,
    errors,
    touched,
    setFieldError
  }: FormikContextType<MediaFormValues> = useFormikContext<MediaFormValues>()

  const images = values.images ?? []
  const imageCount = images.length

  const setImages = (updated: Image[]) => {
    setFieldValue('images', updated)
  }

  const clearErrors = () => {
    setFieldError('images', '')
  }

  const handleFileUpload = async (uploadFiles: File[]) => {
    clearErrors()

    if (!uploadFiles.every(({ size }) => size < 10 * 1024 * 1024)) {
      // bytes
      setFieldError('images', 'File size must be less than 10mb')
      return
    }

    const listingId = values.listing_id
    const unitId = values.unit_id ?? ''

    const filesAllowed = () => {
      if (imageCount > STORAGE_LIMIT) {
        return 0
      } else if (uploadFiles.length + imageCount > STORAGE_LIMIT) {
        return STORAGE_LIMIT - imageCount
      } else {
        return uploadFiles.length
      }
    }

    const allowedUploadFiles = uploadFiles.slice(0, filesAllowed())
    const allowedFilesLength = allowedUploadFiles.length

    if (allowedFilesLength !== 0) {
      const newImages = await dispatch.listing.uploadImages({
        files: allowedUploadFiles,
        unitId,
        imageCount,
        listingId
      })

      allowedFilesLength < uploadFiles.length &&
        toast(
          <Trans
            i18nKey="l.listing.edit.image_upload_limit_exceeded.text"
            values={{
              count: allowedFilesLength,
              max: STORAGE_LIMIT,
              imageLabel: t(
                allowedFilesLength === 1
                  ? 'l.listing.edit.number_of_images.text'
                  : 'l.listing.edit.number_of_images.text_plural'
              )
            }}
            components={{ strong: <strong /> }}
          />,
          {
            title: t('l.listing.edit.image_upload_limit_exceeded.title', 'Uploaded Limit Exceeded'),
            type: 'warning'
          }
        )
      setImages([...images, ...newImages])
    } else if (imageCount >= STORAGE_LIMIT && uploadFiles.length > 0) {
      toast(
        <Trans i18nKey="l.listing.edit.image_upload_failed_max_limit.text">
          Oops! You have reached the limit of <strong>{STORAGE_LIMIT} photos</strong>. Remove some
          first before adding more.
        </Trans>,
        {
          title: t('l.listing.edit.image_upload_failed.title', 'Photos Failed to Upload'),
          type: 'error'
        }
      )
    }
  }

  const handleDeleteImage = async (selected: Image) => {
    clearErrors()

    try {
      await dispatch.listing.deleteUnitFile({
        listingId: values.listing_id,
        unitId: values.unit_id,
        fileId: selected.id
      })
    } catch (err) {
      toast(
        <Trans i18nKey="l.listing.edit.image_delete_failed.text">
          Oops! Something went wrong while trying to delete the image. Please try again later.
        </Trans>,
        {
          title: t('l.listing.edit.image_delete_failed.title', 'Delete Failed'),
          type: 'error'
        }
      )
      return
    }

    const remains = images.filter((item: Image) => item.id !== selected.id)
    setImages(remains)

    if (remains.length > 0 && !remains.some((image: Image) => image.tag === 'cover_photo')) {
      handleSetCoverPhoto(remains[0], remains)
    }
  }

  const toastUpdateError = () => {
    toast(
      <Trans i18nKey="l.listing.edit.image_update_failed.text">
        Oops! Something went wrong while trying to update the image. Please try again later.
      </Trans>,
      {
        title: t('l.listing.edit.image_update_failed.title', 'Update Failed'),
        type: 'error'
      }
    )
  }

  const updateServerCoverPhoto = async ({
    file_id,
    unit_id
  }: {
    file_id: string
    unit_id: string
  }) => {
    let result
    try {
      result = await setCover({
        body: {
          file_id,
          unit_id
        }
      })
      if (!result?.response?.ok) {
        throw Error(result?.response?.body.message)
      }
    } catch (err) {
      console.log(err)
      toastUpdateError()
      return Promise.resolve(false)
    }
    return Promise.resolve(true)
  }

  // `remains` is used when cover photo is removed but Formik doesn't update
  // values.images right away and so it returns stale data.
  const handleSetCoverPhoto = async (selected: Image, remains?: Image[]) => {
    if (!(await updateServerCoverPhoto({ file_id: selected.id, unit_id: data.unit_id }))) {
      return
    }

    const images = remains ?? values.images
    const oldCover = images.find((image: Image) => image.tag === 'cover_photo')

    if (oldCover) {
      if (oldCover.id === selected.id) {
        return // shouldn't hit
      }

      oldCover.tag = 'photo'
    }

    const newCover = images.find((image: Image) => image.id === selected.id)
    if (newCover) {
      newCover.tag = 'cover_photo'
    }

    setImages(
      images.sort((a: Image) => {
        return a.tag === 'cover_photo' ? -1 : 1
      })
    )
  }

  const handleSortImages = (sorted: Image[]) => {
    const sortedWithCoverPhotoUpdated = sorted.map((img, index) => ({
      ...img,
      tag: index === 0 ? 'cover_photo' : `photo-${index}`
    }))
    setImages(sortedWithCoverPhotoUpdated)
  }

  return (
    <FormBox
      label={t('Photos', 'Photos')}
      icon={
        <Counter
          name={t('Photos', 'Photos')}
          count={imageCount}
          max={STORAGE_LIMIT}
          isLoading={isLoading}
        />
      }
    >
      {isLoading ? (
        <FormBoxSkeleton />
      ) : (
        <Stack space="gutter">
          {imageCount > 0 && (
            <DraggablePhotoList
              items={images}
              onChange={handleSortImages}
              onSetCoverPhoto={handleSetCoverPhoto}
              onDelete={handleDeleteImage}
            />
          )}
          <Dropzone
            id="images"
            accept={{
              'image/jpeg': [],
              'image/png': [],
              'application/pdf': []
            }}
            files={[]}
            onChange={files => {
              handleFileUpload(files)
            }}
            onRemoveFile={() => {}}
            message={
              touched?.images &&
              errors?.images && <Box paddingTop="xxsmall">{errors?.images?.toString()}</Box>
            }
            maxFiles={20}
            disabled={imageCount >= STORAGE_LIMIT}
            required
          />
          <InfoBox>
            <Trans i18nKey="l.listing.edit.photos_info.text">
              Listings with better photos receive 3x more inquiries.{' '}
              <TextLink
                href="https://liv.rent/blog/landlords/tips-for-photographing-your-rental-property/#1_Clean_up_and_declutter"
                target="_blank"
              >
                <u>Read more</u>
              </TextLink>
            </Trans>
          </InfoBox>
        </Stack>
      )}
    </FormBox>
  )
}

export default PhotoSection
